import {
  Button,
  Col,
  Flex,
  Form,
  Modal,
  type ModalProps,
  Row,
  Switch,
  Tooltip,
  Typography,
} from 'antd';
import { message } from 'antd';
import { FC, useCallback, useEffect } from 'react';

import { useAdminCompanyController_getAllPromoCodes } from '@api-client/generated/AdminCompanyController/getAllPromoCodes';
import { useAdminCompanyController_updateOneById } from '@api-client/generated/AdminCompanyController/updateOneById';
import { Schemas } from '@api-client/generated/types';
import { CompanyStatus } from '@constants';
import {
  BillingRequirementsAlert,
  CompanyDetailsWrapperFormControl,
} from '@entities';
import { useCurrentCompanyId, useFormValidationGate } from '@hooks';
import {
  getMissingInvoicingFields,
  readBillingSettingsFromForm,
  toBillingEnabled,
  transformToOptions,
  validateBillingSettingsFields,
} from '@utils';

import BillingIntermediationFields from './BillingIntermediationFields';
import * as S from './styled';

const companyStatuses = transformToOptions(CompanyStatus);

/**
 * `canEnableBilling` disables the **Enabled option**, not the select.
 *
 * The rule is "billing may not be turned on for a company that cannot be invoiced", and
 * disabling the whole control enforced something else: it blocked both directions. That left a
 * company whose invoicing details were cleared after billing was switched on with no way to
 * switch it off from the only screen in the backoffice that writes `billingSettings` — and, now
 * that the locked select holds the stored `true`, the price validators run against it, so such a
 * company with an unset base price opened with Save disabled and no escape from the modal at all.
 * Both dead ends come from one rule being enforced in both directions; one disabled option
 * enforces it in the direction it is about.
 */
const billingEnabledOptions = (canEnableBilling: boolean) => [
  {
    label: 'Enabled',
    value: 'true',
    disabled: !canEnableBilling,
  },
  {
    label: 'Disabled',
    value: 'false',
  },
];

const BILLING_DISABLED_MESSAGE =
  'Billing stays disabled until the following invoicing details are filled in on the Company Info tab:';

/**
 * Shown instead when the company stores `isBillingEnabled: true` and has since lost an
 * invoicing field: the select below reads Enabled and is locked, so "billing stays
 * disabled" would be the wrong thing to say about it. What is blocked is the invoice.
 */
const BILLING_LOCKED_ON_MESSAGE =
  'Billing is enabled for this company, but no invoice can be issued until the following invoicing details are filled in on the Company Info tab:';

const PAYSLIPS_ACCESS_TOOLTIP =
  'When enabled, clients see the payslips tab and can access their payroll documents.';

const PayslipsAccessSwitch: FC<{
  checked?: boolean;
  onChange?: (checked: boolean) => void;
}> = ({ checked, onChange }) => (
  <Tooltip title={PAYSLIPS_ACCESS_TOOLTIP}>
    <span>
      <Switch checked={!!checked} onChange={onChange} />
    </span>
  </Tooltip>
);

type CompanyDetailsServiceInfoEditProps = {
  company: Schemas.AdminCompanyResponseDto;
  onCancel: () => void;
  onAfterEdit: () => void;
} & ModalProps;

const CompanyDetailsServiceInfoEdit: FC<CompanyDetailsServiceInfoEditProps> = ({
  company,
  open,
  onAfterEdit,
  onCancel,
  ...rest
}) => {
  const companyId = useCurrentCompanyId();

  // Billing can only be *turned on* once the company has all the data required to issue an
  // invoice. While that is incomplete the Enabled option is disabled, so nobody can enable it
  // here — and switching it off stays available, which is the direction the rule says nothing
  // about (the missing fields themselves are edited on the Company Info tab, not in this modal).
  const missingInvoicingFields = getMissingInvoicingFields(company);
  const canEnableBilling = missingInvoicingFields.length === 0;

  // The select shows -- and so still saves -- the stored setting, so a company that already had
  // billing on keeps it. See the load effect below (ENG-1348).
  const storedBillingEnabled = toBillingEnabled(
    company.billingSettings?.isBillingEnabled
  );

  const [form] = Form.useForm();

  // Revalidates on every change, and ignores the outcome of a round a later change
  // superseded -- otherwise a stale result settling last leaves Save disabled on a
  // valid form.
  const { isSubmitDisabled, revalidate } = useFormValidationGate(form, !!open);

  const { mutate: updateCompany, isPending: loading } =
    useAdminCompanyController_updateOneById();

  const { data: promoCodes, isLoading: isLoadingPromoCodes } =
    useAdminCompanyController_getAllPromoCodes();

  useEffect(
    () => {
      if (company && open) {
        // Convert isBillingEnabled boolean to string to match select option values.
        //
        // The stored value, always. It used to be forced to 'false' whenever the company
        // could not be invoiced, which is where ENG-1348 came from: Save submits the form,
        // so that forced value was what got stored -- and it left the select claiming
        // billing was off for a company that had it on. Refusing to *enable* billing on a
        // company that cannot be invoiced is the select's `disabled`, not a value written
        // over the stored one, and unlike the overwrite it also keeps the price validators
        // judging the setting that is actually in force.
        form.setFieldsValue({
          ...company,
          isPayrollEnabled: company.isPayrollEnabled ?? false,
          billingSettings: {
            ...company.billingSettings,
            isBillingEnabled: String(storedBillingEnabled),
          },
        });
        // Run validation immediately to show errors on required fields
        revalidate();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [company, open]
  );

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  /**
   * Creates a billing settings object from form values for validation.
   */
  const getBillingSettingsFromForm = useCallback(
    () => readBillingSettingsFromForm(form),
    [form]
  );

  /**
   * Whether billing is enabled according to the form.
   *
   * The form holds a STRING for this key in every state it can be in — the select's options are
   * `'true'`/`'false'` and the load effect writes a `String()` of the stored boolean — so the
   * coercion in `readBillingSettingsFromForm` is what makes this a boolean. (The boolean arm of
   * that coercion is for a stored blob, not for anything this form holds.)
   */
  const isBillingEnabledInForm = useCallback(
    () => getBillingSettingsFromForm().isBillingEnabled,
    [getBillingSettingsFromForm]
  );

  /**
   * Sends the three things this form owns, and nothing else.
   *
   * It used to send `{ ...company, ...values }` — the whole company object as it looked when the
   * modal opened, `id`, `details` and `createdAt` included. `companies.update` is a partial update
   * at column level, so that rewrote every column with a value that could be minutes stale: two
   * CSMs on two tabs, and the second save silently reverted the first one's edit to a field this
   * form does not even show. `billingSettings` is spread over the stored object rather than
   * replacing it, because it is one jsonb column and the form holds a subset of its keys — the
   * same reason the DTO's own comment gives for making the intermediation keys optional.
   *
   * `isBillingEnabled` is a value the operator can see in the form — which the key it replaced
   * was not.
   * That is the whole of the ENG-1348 fix: the load effect used to force that select to
   * Disabled on a company missing an invoicing field, and since Save submits the form, a CSM
   * who opened the modal only to flip Payslips access stored that forced `false` and turned
   * billing off for a company that had it on — silently, `billing_service` returning early on
   * `!isBillingEnabled` from then on. Sending something other than the form's value would
   * only move that mismatch: the fix is that the locked select now holds the stored setting,
   * which is also what the price validators read.
   */
  const handleSubmit = (values: Schemas.AdminCompanyResponseDto) => {
    updateCompany(
      {
        parameter: {
          id: companyId!,
        },
        requestBody: {
          status: values.status,
          isPayrollEnabled: values.isPayrollEnabled,
          // The cast is doing more than reconciling two partial spreads, and an earlier version of
          // this comment claimed otherwise. It also absorbs a genuinely incomplete object: a
          // company whose `billing_settings` is absent submits `{ isBillingEnabled: false }` and
          // nothing else, because the four price fields are only registered once they have a
          // value — remove the cast and tsc names all four. And clearing one of them puts `null`
          // on the wire where the type says `number`, which the backend's DTO neither coerces nor
          // refuses (that key carries no decorator at all). Both predate this form's payload being
          // narrowed and neither is fixed here; what is fixed is the comment that denied them.
          billingSettings: {
            ...company.billingSettings,
            ...values.billingSettings,
            // The select's options are the strings 'true'/'false', and the load effect writes a
            // String() of the stored boolean, so this key is a string in every form state. The
            // backend's DTO coerces it (`@Transform(value === 'true' || value === true)`, put
            // there for this form), so the string is handled rather than broken — but the request
            // should still carry what its own type promises. Read past the spread above, which
            // carries the form's raw string for this key.
            isBillingEnabled: isBillingEnabledInForm(),
          } as Schemas.BillingSettingsDto,
        },
      },
      {
        onSuccess: () => {
          message.success('Company information has been successfully updated');

          onAfterEdit();
          handleCancel();
        },
      }
    );
  };

  return (
    <Modal
      width={575}
      open={open}
      title="Edit service info"
      footer={null}
      onCancel={handleCancel}
      destroyOnClose
      centered
      {...rest}
    >
      <Form
        form={form}
        onFinish={handleSubmit}
        layout="vertical"
        requiredMark={false}
      >
        <BillingRequirementsAlert
          missingFields={missingInvoicingFields}
          message={
            storedBillingEnabled
              ? BILLING_LOCKED_ON_MESSAGE
              : BILLING_DISABLED_MESSAGE
          }
        />

        <S.Card>
          <Typography.Title level={5}>Billing settings</Typography.Title>

          <Row gutter={[12, 0]}>
            <CompanyDetailsWrapperFormControl
              type="select"
              span={7}
              form={{
                label: 'Status',
                name: 'status',
              }}
              control={{
                options: companyStatuses,
              }}
            />

            <CompanyDetailsWrapperFormControl
              type="select"
              span={7}
              form={{
                label: 'Billing enabled',
                name: ['billingSettings', 'isBillingEnabled'],
              }}
              control={{
                options: billingEnabledOptions(canEnableBilling),
              }}
            />
          </Row>

          <Row gutter={[12, 0]}>
            <Col span={10}>
              <Form.Item
                label="Payslips access"
                name="isPayrollEnabled"
                valuePropName="checked"
              >
                <PayslipsAccessSwitch />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[12, 0]}>
            <CompanyDetailsWrapperFormControl
              type="input-number"
              span={10}
              form={{
                label: 'Base period price',
                name: ['billingSettings', 'baseServicePrice'],
                dependencies: [['billingSettings', 'isBillingEnabled']],
                rules: [
                  {
                    validator: () => {
                      if (!isBillingEnabledInForm()) {
                        return Promise.resolve();
                      }
                      const validation = validateBillingSettingsFields(
                        getBillingSettingsFromForm()
                      );
                      if (validation.errors.baseServicePrice) {
                        return Promise.reject(
                          new Error(validation.errors.baseServicePrice)
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ],
              }}
              control={{
                style: { width: '100%' },
                min: 0,
              }}
            />

            <CompanyDetailsWrapperFormControl
              type="input-number"
              span={10}
              form={{
                label: 'Documents included',
                name: ['billingSettings', 'documentsIncluded'],
                dependencies: [['billingSettings', 'isBillingEnabled']],
                rules: [
                  {
                    validator: () => {
                      if (!isBillingEnabledInForm()) {
                        return Promise.resolve();
                      }
                      const validation = validateBillingSettingsFields(
                        getBillingSettingsFromForm()
                      );
                      if (validation.errors.documentsIncluded) {
                        return Promise.reject(
                          new Error(validation.errors.documentsIncluded)
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ],
              }}
              control={{
                style: { width: '100%' },
                min: 0,
              }}
            />
          </Row>

          <Row gutter={[12, 0]}>
            <CompanyDetailsWrapperFormControl
              type="input-number"
              span={10}
              form={{
                label: 'Payslip price',
                name: ['billingSettings', 'payslipPrice'],
                dependencies: [
                  ['billingSettings', 'isBillingEnabled'],
                  ['billingSettings', 'payslipAmount'],
                ],
                rules: [
                  {
                    validator: () => {
                      if (!isBillingEnabledInForm()) {
                        return Promise.resolve();
                      }
                      const validation = validateBillingSettingsFields(
                        getBillingSettingsFromForm()
                      );
                      if (validation.errors.payslipPrice) {
                        return Promise.reject(
                          new Error(validation.errors.payslipPrice)
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ],
              }}
              control={{
                style: { width: '100%' },
                min: 0,
              }}
            />

            <CompanyDetailsWrapperFormControl
              type="input-number"
              span={10}
              form={{
                label: 'Payslips amount',
                name: ['billingSettings', 'payslipAmount'],
                dependencies: [
                  ['billingSettings', 'isBillingEnabled'],
                  ['billingSettings', 'payslipPrice'],
                ],
                rules: [
                  {
                    validator: () => {
                      if (!isBillingEnabledInForm()) {
                        return Promise.resolve();
                      }
                      const validation = validateBillingSettingsFields(
                        getBillingSettingsFromForm()
                      );
                      if (validation.errors.payslipAmount) {
                        return Promise.reject(
                          new Error(validation.errors.payslipAmount)
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ],
              }}
              control={{
                style: { width: '100%' },
                min: 0,
              }}
            />
          </Row>

          <BillingIntermediationFields
            getBillingSettings={getBillingSettingsFromForm}
          />

          <Row align="middle" gutter={[12, 0]}>
            <CompanyDetailsWrapperFormControl
              type="select"
              span={10}
              form={{
                label: 'Promo code',
                name: ['billingSettings', 'promoCode'],
              }}
              control={{
                options:
                  promoCodes?.map((code: Schemas.AdminPromoCodeDto) => ({
                    value: code.name,
                  })) || [],
                loading: isLoadingPromoCodes,
                allowClear: true,
                placeholder: 'Select promo code',
              }}
            />
          </Row>
        </S.Card>

        <S.Submit>
          <Form.Item noStyle>
            <Flex justify="flex-end" gap={12}>
              <Button onClick={handleCancel} size="large">
                Cancel
              </Button>

              <Button
                type="primary"
                htmlType="submit"
                size="large"
                loading={loading}
                disabled={isSubmitDisabled}
              >
                Save
              </Button>
            </Flex>
          </Form.Item>
        </S.Submit>
      </Form>
    </Modal>
  );
};

export default CompanyDetailsServiceInfoEdit;
